import { PAGINATION_DEFAULT_ROWS_PER_PAGE } from 'attrs/pagination';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadExpiredLicenses } from 'redux/licenses/actions';
import { getExpiredLicenses } from 'redux/licenses/selectors';
import { Column, Order, Table, MultiSelect, MultiSelectOption, StaticIcon } from 'web-components-ts';
import { Select, MenuItem, SelectChangeEvent, FormControl, InputLabel, TextField } from '@mui/material';
import { LICENSES_EXPIRATION_DATES, LICENSES_PLANS, LICENSES_SUPPORT_NAMES } from 'attrs/licenses';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { COLOR_PRIMARY } from 'attrs/colors';
import { formatLocalizedDateTime } from 'helpers/dateLocale';

const labelAmmend = 'dashboard.tabs.expired_licenses';

interface IExpiredLicense {
  product: string;
  customer_name: string;
  support_name: string;
  customer_id: string;
  plan: string;
  note: string;
  expiration_date: string;
}

const ExpiredLicenses = () => {
  window.history.replaceState(null, '', '/dashboard/expired_licenses');
  const history = useHistory();

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const firstPage = 1;
  const [currentPage, setCurrentPage] = useState(firstPage);
  const [totalPages, setTotalPages] = useState(0);
  const [expiredLicensesList, setExpiredLicensesList] = useState<IExpiredLicense[]>([]);
  const [sortBy, setSortBy] = useState('customer_name');
  const [order, setOrder] = useState('ASC');
  const [expirationDateFilter, setExpirationDateFilter] = useState('');
  const [supportNameFilter, setSupportNameFilter] = useState('');
  const [licenseNoteFilter, setLicenseNoteFilter] = useState('');
  const [licensePlanMultipleFilter, setLicensePlanMultipleFilter] = useState<{ title: string; value: string }[]>([]);

  const rowsPerPage = 5;

  const { data: getExpiredLicensesResponse } = useSelector(getExpiredLicenses);

  useEffect(() => {
    dispatch(
      loadExpiredLicenses({
        limit: PAGINATION_DEFAULT_ROWS_PER_PAGE,
        page: currentPage,
        order,
        sort: sortBy
      })
    );
  }, [currentPage, dispatch, order, sortBy]);

  const columns: Column<IExpiredLicense>[] = [
    {
      field: 'product',
      label: t(`${labelAmmend}.table_headers.product`),
      sort: false,
      align: 'right',
      render: () => <StaticIcon name="NotifyLogo" sx={{ color: COLOR_PRIMARY }} />
    },
    {
      field: 'customer_name',
      label: t(`${labelAmmend}.table_headers.customer_name`),
      sort: true,
      align: 'left'
    },
    {
      field: 'support_name',
      label: t(`${labelAmmend}.table_headers.support_name`),
      sort: false,
      align: 'left'
    },
    {
      field: 'plan',
      label: t(`${labelAmmend}.table_headers.plan`),
      sort: false,
      align: 'left',
      render: (license: IExpiredLicense) => <span> {t(`licenses.${license.plan.toLocaleLowerCase()}`)}</span>
    },
    {
      field: 'note',
      label: t(`${labelAmmend}.table_headers.note`),
      sort: false,
      align: 'left',
      size: '10%'
    },
    {
      field: 'expiration_date',
      label: t(`${labelAmmend}.table_headers.expiration_date`),
      sort: true,
      align: 'left',
      render: (license: IExpiredLicense) =>
        formatLocalizedDateTime(
          license?.expiration_date,
          (expirationDate: { date: string; time: string }) =>
            t('dashboard.tabs.gateway.machine_without_gateway.table.machine_creation_date_value', {
              ...expirationDate,
              interpolation: { escapeValue: false }
            })
        )
    }
  ];

  useEffect(() => {
    if (getExpiredLicensesResponse?.data?.length) {
      setCurrentPage(getExpiredLicensesResponse.page || 0);
      setTotalPages(getExpiredLicensesResponse.total_pages || 0);
      setExpiredLicensesList(getExpiredLicensesResponse.data);
    } else {
      setExpiredLicensesList([]);
    }
  }, [getExpiredLicensesResponse]);

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
    dispatch(
      loadExpiredLicenses({
        page,
        order,
        sort: sortBy,
        expiration_date: expirationDateFilter,
        support_name: supportNameFilter,
        license_plan: licensePlanMultipleFilter.map(item => item.value),
        license_note: licenseNoteFilter
      })
    );
  };

  const handleTableSort = (orderParam: Order, orderByParam: keyof IExpiredLicense) => {
    setSortBy(orderByParam);
    setOrder(orderParam.toUpperCase());
    dispatch(
      loadExpiredLicenses({
        order: orderParam.toUpperCase(),
        sort: orderByParam,
        expiration_date: expirationDateFilter,
        support_name: supportNameFilter,
        license_plan: licensePlanMultipleFilter.map(item => item.value),
        license_note: licenseNoteFilter
      })
    );
  };

  const handleSetExpirationDateFilter = (event: SelectChangeEvent<string>) => {
    const selectedExpirationDateFilter = event.target.value;
    setExpirationDateFilter(selectedExpirationDateFilter);
    setCurrentPage(firstPage);

    dispatch(
      loadExpiredLicenses({
        order,
        sort: sortBy,
        expiration_date: selectedExpirationDateFilter,
        support_name: supportNameFilter,
        license_plan: licensePlanMultipleFilter.map(item => item.value),
        license_note: licenseNoteFilter
      })
    );
  };

  const handleSetSupportNameFilter = (event: SelectChangeEvent<string>) => {
    const selectedSupportNameFilter = event.target.value;
    setSupportNameFilter(selectedSupportNameFilter);
    setCurrentPage(firstPage);

    dispatch(
      loadExpiredLicenses({
        order,
        sort: sortBy,
        expiration_date: expirationDateFilter,
        support_name: selectedSupportNameFilter,
        license_plan: licensePlanMultipleFilter.map(item => item.value),
        license_note: licenseNoteFilter
      })
    );
  };

  const handleSetLicensePlanMultipleFilter = (event: string, value: { value: string; title: string }[]) => {
    const selectedLicensePlanFilter = value;
    setLicensePlanMultipleFilter(selectedLicensePlanFilter);
    setCurrentPage(firstPage);

    dispatch(
      loadExpiredLicenses({
        order,
        sort: sortBy,
        expiration_date: expirationDateFilter,
        support_name: supportNameFilter,
        license_plan: selectedLicensePlanFilter.map(item => item.value),
        license_note: licenseNoteFilter
      })
    );
  };

  const handleSetLicenseNoteFilter = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const selectedLicenseNoteFilter = event.target.value;
    setLicenseNoteFilter(selectedLicenseNoteFilter);
    setCurrentPage(firstPage);

    dispatch(
      loadExpiredLicenses({
        order,
        sort: sortBy,
        expiration_date: expirationDateFilter,
        support_name: supportNameFilter,
        license_plan: licensePlanMultipleFilter.map(item => item.value),
        license_note: selectedLicenseNoteFilter
      })
    );
  };

  const handleSelectRow = (row: IExpiredLicense) => {
    const routePath = `/customers/${row.customer_id}`;
    history.push(routePath);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', marginBottom: '2em', gap: '1em' }}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="expiration_date_filter_label">
            {t(`dashboard.tabs.expired_licenses.filter_by_expiration_date.placeholder`)}
          </InputLabel>
          <Select
            fullWidth
            labelId="expiration_date_filter_label"
            id="expiration_date_filter"
            value={expirationDateFilter}
            onChange={handleSetExpirationDateFilter}
          >
            <MenuItem key={LICENSES_EXPIRATION_DATES.EXPIRED} value={LICENSES_EXPIRATION_DATES.EXPIRED}>
              {t(`${labelAmmend}.filter_by_expiration_date.${LICENSES_EXPIRATION_DATES.EXPIRED}`)}
            </MenuItem>
            <MenuItem
              key={LICENSES_EXPIRATION_DATES.EXPIRED_IN_30_DAYS}
              value={LICENSES_EXPIRATION_DATES.EXPIRED_IN_30_DAYS}
            >
              {t(`${labelAmmend}.filter_by_expiration_date.${LICENSES_EXPIRATION_DATES.EXPIRED_IN_30_DAYS}`)}
            </MenuItem>
            <MenuItem
              key={LICENSES_EXPIRATION_DATES.EXPIRED_IN_60_DAYS}
              value={LICENSES_EXPIRATION_DATES.EXPIRED_IN_60_DAYS}
            >
              {t(`${labelAmmend}.filter_by_expiration_date.${LICENSES_EXPIRATION_DATES.EXPIRED_IN_60_DAYS}`)}
            </MenuItem>
            <MenuItem
              key={LICENSES_EXPIRATION_DATES.EXPIRED_IN_90_DAYS}
              value={LICENSES_EXPIRATION_DATES.EXPIRED_IN_90_DAYS}
            >
              {t(`${labelAmmend}.filter_by_expiration_date.${LICENSES_EXPIRATION_DATES.EXPIRED_IN_90_DAYS}`)}
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="filled" fullWidth>
          <InputLabel id="support_name_filter_label">
            {t(`dashboard.tabs.expired_licenses.filter_by_support_name.placeholder`)}
          </InputLabel>
          <Select
            fullWidth
            labelId="support_name_filter_label"
            id="support_name_filter"
            value={supportNameFilter}
            onChange={handleSetSupportNameFilter}
          >
            {LICENSES_SUPPORT_NAMES.map(name => (
              <MenuItem key={name} value={name}>
                {name === 'All NETZSCH'
                  ? t('dashboard.tabs.expired_licenses.filter_by_support_name.all_netzsch')
                  : name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <MultiSelect
          optionType="checkbox"
          label={t(`dashboard.tabs.expired_licenses.filter_by_license_plan.placeholder`)}
          name="license_plan_filter"
          id="license_plan_filter"
          options={LICENSES_PLANS.map(item => ({ title: t(`licenses.${item.toLocaleLowerCase()}`), value: item }))}
          setFieldValue={
            handleSetLicensePlanMultipleFilter as (
              event: string,
              value: MultiSelectOption[]
            ) => void
          }
          value={licensePlanMultipleFilter}
          sx={{
            input: { bgcolor: 'transparent !important' },
            '.MuiInputBase-root': { bgcolor: 'rgba(0, 0, 0, 0.06) !important' }
          }}
        />

        <TextField
          fullWidth
          type="text"
          id="license-note"
          label={t(`dashboard.tabs.expired_licenses.filter_by_license_note.placeholder`)}
          variant="filled"
          value={licenseNoteFilter}
          onChange={handleSetLicenseNoteFilter}
        />
      </div>

      <Table<IExpiredLicense>
        rowKey="id"
        dataSource={expiredLicensesList}
        columns={columns}
        hasFooter
        itensPerPageLabel={t('general.overview.items')}
        itemLabel={t('general.overview.of')}
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleTableSort={handleTableSort}
        rowClick={handleSelectRow}
      />
    </div>
  );
};

export default ExpiredLicenses;
